import { toast } from 'react-toastify';

const TimeSlotSelector = ({
  selectedDate,
  timeSlots,
  selectedSlots,
  amenitySettings,
  shouldDisableDate,
  onSlotsChange
}) => {
  // Helper functions
  const timeToMinutes = (timeStr) => {
    const [hours, minutes] = timeStr.split(':').map(Number);
    return hours * 60 + minutes;
  };

  const wouldExceedMaxDuration = (newSlots) => {
    if (!amenitySettings?.maxDurationMinutes) return false;
    const slotSizeMinutes = amenitySettings.slotSizeMinutes || 30;
    const maxSlots = Math.floor(amenitySettings.maxDurationMinutes / slotSizeMinutes);
    return newSlots.length > maxSlots;
  };

  const getSelectedDuration = () => {
    if (selectedSlots.length === 0) return 0;
    const slotSizeMinutes = amenitySettings?.slotSizeMinutes || 30;
    return selectedSlots.length * slotSizeMinutes;
  };

  const toggleSlotSelection = (time) => {
    const clickedSlot = timeSlots.find(s => s.time === time);

    onSlotsChange((prev) => {
      // if empty, allow any slot
      if (prev.length === 0) return [time];

      const selectedSlotsWithGroup = prev.map(t => timeSlots.find(s => s.time === t));

      // enforce group restriction
      const selectedGroup = selectedSlotsWithGroup[0]?.groupId;
      if (clickedSlot.groupId !== selectedGroup) {
        toast.error("You cannot select slots across different time ranges.");
        return prev; 
      }
    
      if (prev.includes(time)) {
        const clickedMinutes = timeToMinutes(time);
        return prev.filter(slot => timeToMinutes(slot) < clickedMinutes);
      } else {
        if (prev.length === 0) {
          return [time];
        }

        const selectedMinutes = prev.map(timeToMinutes).sort((a, b) => a - b);
        const minSelected = selectedMinutes[0];
        const maxSelected = selectedMinutes[selectedMinutes.length - 1];
        const clickedMinutes = timeToMinutes(time);

        let slotsToSelect = [];

        if (clickedMinutes < minSelected) {
          for (let slot of timeSlots) {
            const slotMinutes = timeToMinutes(slot.time);
            if (slotMinutes >= clickedMinutes && slotMinutes <= maxSelected && slot.available) {
              slotsToSelect.push(slot.time);
            }
          }
        } else if (clickedMinutes > maxSelected) {
          for (let slot of timeSlots) {
            const slotMinutes = timeToMinutes(slot.time);
            if (slotMinutes >= minSelected && slotMinutes <= clickedMinutes && slot.available) {
              slotsToSelect.push(slot.time);
            }
          }
        } else {
          return prev.includes(time) ? prev.filter(t => t !== time) : [...prev, time];
        }

        if (wouldExceedMaxDuration(slotsToSelect)) {
          toast.error(`Maximum booking duration is ${amenitySettings.maxDurationMinutes} minutes`);
          return prev;
        }

        return slotsToSelect;
      }
    });
  };

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
        <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
        Available Times
        {selectedDate && (
          <span className="text-sm font-normal text-gray-600 ml-2">
            for {selectedDate.format('MMM D, YYYY')}
          </span>
        )}
      </h3>

      {!selectedDate ? (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
          <div className="text-3xl mb-3 text-gray-400">🕐</div>
          <p className="text-gray-600">Please select a date to view available time slots</p>
        </div>
      ) : shouldDisableDate(selectedDate) ? (
        <div className="bg-red-50 border border-red-200 rounded-lg p-8 text-center">
          <div className="text-3xl mb-3 text-red-400">❌</div>
          <p className="text-red-600 font-medium">This date is not available</p>
          <p className="text-red-500 text-sm mt-2">
            This amenity can only be booked on: {amenitySettings?.allowedDaysOfWeek?.join(', ')}
          </p>
        </div>
      ) 
      // : !isRoleAllowed ? (
      //   <div className="bg-amber-50 border border-amber-200 rounded-lg p-8 text-center">
      //       <div className="text-3xl mb-3 text-amber-400">⚠️</div>
      //       <p className="text-amber-700 font-medium">Sorry!</p>
      //       <p className="text-amber-600 text-sm mt-2">
      //           This amenity is not available for you to book at the moment.
      //       </p>
      //   </div>
      // ) 
      : (
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="mb-4">
            <div className="flex items-center gap-4 text-sm text-gray-600 mb-3">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 border-2 border-blue-500 rounded"></div>
                <span>Available</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-blue-500 rounded"></div>
                <span>Selected</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-gray-300 rounded"></div>
                <span>Unavailable</span>
              </div>
            </div>

            {selectedSlots.length > 0 && (
              <div className="text-sm text-gray-600">
                <span className="font-medium">Duration: {getSelectedDuration()} minutes</span>
                {amenitySettings?.maxDurationMinutes && (
                  <span className="ml-2">
                    (Max: {amenitySettings.maxDurationMinutes} minutes)
                  </span>
                )}
              </div>
            )}
          </div>

          {timeSlots.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
                No time slots available for this date
            </div>
            ) : (
            <div className="space-y-6">
                {Object.values(
                    timeSlots.reduce((acc, slot) => {
                        acc[slot.groupId] = acc[slot.groupId] || [];
                        acc[slot.groupId].push(slot);
                        return acc;
                    }, {})
                    ).map((rangeSlots, rangeIndex) => (
                <div
                    key={rangeIndex}
                    className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3"
                >
                    {rangeSlots.map((slot, index) => {
                        const isSelected = selectedSlots.includes(slot.time);
                        const isAvailable = slot.available;

                    return (
                        <button
                        key={index}
                        onClick={() => toggleSlotSelection(slot.time)}
                        disabled={!isAvailable}
                        className={`
                            px-4 py-3 rounded-lg font-medium text-sm transition-all duration-200 border-2
                            ${!isAvailable
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200'
                            : isSelected
                                ? 'bg-blue-500 text-white border-blue-500 shadow-md transform scale-105'
                                : 'bg-white text-blue-600 border-blue-200 hover:border-blue-400 hover:bg-blue-50 hover:shadow-sm'
                            }
                        `}
                        style={{
                            backgroundColor: !isAvailable
                            ? '#E8E8E8'
                            : isSelected
                                ? '#445485'
                                : 'white',
                            borderColor: !isAvailable
                            ? '#E8E8E8'
                            : isSelected
                                ? '#445485'
                                : '#818FB4',
                            color: !isAvailable
                            ? '#818FB4'
                            : isSelected
                                ? 'white'
                                : '#445485'
                        }}
                        >
                        {slot.time}
                        </button>
                    );
                    })}
                </div>
                ))}
            </div>
            )}

          {selectedSlots.length > 0 && (
            <div className="mt-4 p-3 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-800 font-medium">
                Selected: {selectedSlots.join(', ')}
                <span className="ml-2 text-blue-600">
                  ({getSelectedDuration()} minutes)
                </span>
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TimeSlotSelector;